import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { TableSelector } from './shared/common';
import { TableSelectorServices } from './shared/common/TableSelector';
import { PhotoLayerTable } from '@datum-map/Layer/PhotoLayer/types';
import { LayerCollection, PhotoLayer, MapProperties } from '@datum-map';
import { Table as ApiTable, Table } from '@datum-group/isogd-openapi/lib';

const QUERY = { geometry_type__isnull: false };
const LAYER_NAME = 'photoMaterial';
const LAYER_DESCRIPTION = 'Слой с фотоматериалами';
export class PhotoMaterial {
  @observable readonly selectors: TableSelector[] = [];
  @observable private _isLoading = false;
  @observable private _error?: string = undefined;
  private _services: TableSelectorServices;
  private _layerCollection: LayerCollection;
  private _mapProperties: MapProperties;

  constructor(
    services: TableSelectorServices,
    mapProperties: MapProperties,
    layerCollection: LayerCollection,
  ) {
    this._mapProperties = mapProperties;
    this._layerCollection = layerCollection;
    this._services = services;
    const select = new TableSelector(services, QUERY);
    this.selectors.push(select);
    makeObservable(this);
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  @computed get error() {
    return this._error;
  }

  @computed get sources() {
    const ids: string[] = [];

    this.selectors.forEach((selector) => {
      const { value } = selector;
      if (value !== undefined) ids.push(value.table_name);
    });

    const unique = Array.from(new Set(ids));

    return unique;
  }

  @computed get hasSelectedSources() {
    return this.sources.length > 0;
  }

  @action clear = () => {
    const select = new TableSelector(this._services, QUERY);
    this.selectors.splice(0, this.selectors.length);
    this.selectors.push(select);
  };

  @action add = () => {
    const selector = new TableSelector(this._services, QUERY);
    this.selectors.push(selector);
  };

  @action delete = (index: number) => {
    if (index < 0 || index > this.selectors.length - 1)
      throw new Error('Индекса не существует');

    this.selectors.splice(index, 1);
  };

  @action submit = async () => {
    if (!this.hasSelectedSources) return;

    this._isLoading = true;
    this._error = undefined;

    try {
      const tables = this._getUniqueTablesFromSelector();
      const tablesWithGeom = this._getTablesWithGeom(tables);
      const id = this._getPhotoLayerAmountOnMap();
      const name = `${LAYER_NAME}-${id}`;
      const description = `${LAYER_DESCRIPTION}-${id}`;

      const layer = new PhotoLayer(
        {
          tables: tablesWithGeom,
          name,
          description,
        },
        this._mapProperties.map.getView().getProjection().getCode(),
      );
      this._layerCollection.add(layer);
    } catch (e) {
      runInAction(() => (this._error = 'Не удалось загрузить данные для слоя'));
    } finally {
      runInAction(() => (this._isLoading = false));
    }
  };

  private _getUniqueTablesFromSelector() {
    const tables: Record<string, Table> = {};

    this.selectors.forEach((selector) => {
      const { value } = selector;

      if (!value) return;

      if (tables[value.table_name]) return;

      tables[value.table_name] = value;
    });

    return Object.values(tables);
  }

  private _getTablesWithGeom(tables: ApiTable[]): PhotoLayerTable[] {
    const result: PhotoLayerTable[] = [];

    tables.forEach((table) => {
      if (table.geometry_type) {
        result.push({ ...table, geometry_type: table.geometry_type });
      }
    });

    return result;
  }

  private _getPhotoLayerAmountOnMap() {
    let amount = 0;

    this._layerCollection.layers.forEach((layer) => {
      if (layer instanceof PhotoLayer) amount++;
    });

    return amount;
  }
}
